import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';
import { SubscriptionsStateService } from '@brand-lolly/app/billing/services/subscriptions-state.service';
import { ProductAlias } from '@brand-lolly/app/billing/types/billing.enums';
import { requireGatedNavigationAccess } from '@brand-lolly/app/shared/navigation/navigation-product-access';

/**
 * NOTHING IS BLOCKED OUTRIGHT, and that is the rule this file now implements
 * rather than a summary of it.
 *
 * Three guards remain, and each of them lands the brand on a PAGE — the
 * department's own full-screen upsell, inside the shell — rather than putting a
 * modal in front of a door. What used to live here and no longer does:
 *
 * - `subscriptionRequiredGuard`, which opened the paywall dialog and bounced to
 *   the plans page. It gated `/messaging`, `/negotiation`, `/discovery` and
 *   `/list` on `ProductAlias.DISCOVERY`. That product is retired: the catalogue
 *   is one `application` product and `feature.discovery` is seeded on every tier
 *   including the free one, so the gate could not be satisfied by the customers
 *   who had just paid and could not be bought out of by anyone. The backend
 *   dropped its matching gates in the same change;
 * - `anySubscriptionGuard`, which closed EVERY product page for a brand holding
 *   no subscription at all. Under the unified catalogue that population is the
 *   free tier — nothing grants a subscription on `application_free`; a free
 *   brand gets a credit account and an allowance — so the guard turned the free
 *   tier into a locked app. What bounds a free brand is its credit ceiling, at
 *   the action, not at the door;
 * - `partnerAdsAccessGuard`, removed 2026-09-15 with the tier gate it enforced.
 *   Partner Ads backs the creators and affiliates a free account already has, so
 *   the owner opened it on every tier; `feature.partner_ads` is seeded `true`
 *   everywhere and the backend dropped the dependency from the whole `meta_ads`
 *   tree, so the guard's only remaining refusal would have been aimed at brands
 *   the API now serves. Its upsell page went with it — a full screen offering an
 *   upgrade for a department the brand can already open is worse than no page.
 *
 * SOCIAL LISTENING IS THE ONE PRODUCT A TIER GENUINELY WITHHOLDS (Business only,
 * signed off 2026-09-10). It is gated here because its API refuses per request —
 * a page that loads and then says "could not load, try again" on every panel is
 * a worse answer than the offer.
 *
 * NEITHER THE PRODUCT NOR THE PAGE IS NAMED HERE ANY MORE. Both are read from
 * `NAVIGATION_PRODUCT_ACCESS`, the one table the sidenav reads too, so an entry
 * cannot be closed in the menu and open on its route, or the reverse — which is
 * the failure that left Campaigns with an error toast for a treatment.
 */

/**
 * Every gate below asks this first, and lets the brand through when it answers
 * true: where the app could not establish what the brand holds, the client has
 * no business closing a department. The refusal, if there is one, comes from the
 * request — which is exactly what the backend guard does with the same doubt
 * (`app/billing/tier/access.py` logs an internal failure and grants access).
 *
 * It has to be asked explicitly because `SubscriptionsStateService.ensureLoaded`
 * answers a failed billing read with the snapshot it already holds (empty on a
 * first load) rather than an error, so `hasProductAccess` reports "no" for a
 * brand that could not be read exactly as it does for a brand that holds
 * nothing. `loadFailed` is the only thing separating the two; the `catchError`
 * at the end of each gate covers only an error that escapes the store itself.
 */
function billingStateIsUnknown(state: SubscriptionsStateService): boolean {
	return state.loadFailed;
}

/**
 * Build the access gate for one product: admit the brand, or send it to that
 * department's in-shell upsell page.
 *
 * Decided by `SubscriptionsStateService.canEnterProduct` — the same rule the
 * sidenav entry asks, unknown state included (see {@link billingStateIsUnknown}).
 *
 * The upsell route MUST be mounted before the department's own lazy route and
 * MUST NOT carry this guard, or the redirect loops.
 */
function productAccessGuard(
	productAlias: ProductAlias,
	upsellPath: string
): CanActivateFn {
	return (): Observable<boolean | UrlTree> => {
		const subscriptionsState = inject(SubscriptionsStateService);
		const router = inject(Router);

		return subscriptionsState.ensureLoaded().pipe(
			map(() =>
				subscriptionsState.canEnterProduct(productAlias)
					? true
					: router.parseUrl(upsellPath)
			),
			catchError(() => of(true))
		);
	};
}

/** Stated once, in the table the sidenav reads too. */
const SOCIAL_LISTENING_ACCESS =
	requireGatedNavigationAccess('social-listening');
const AFFILIATES_ACCESS = requireGatedNavigationAccess('affiliates');

/**
 * Social Listening access gate — the one product the tier really withholds
 * (`feature.social_listening` is Business only). A brand whose tier does not
 * carry it lands on `/social-listening/upgrade`: the full-screen in-shell page
 * that says what the product does and offers the plan, which is the shape the
 * owner asked for — the click reaches the section, and the explanation is inside
 * it. An unreadable billing state fails open — see {@link billingStateIsUnknown}.
 */
export const socialListeningAccessGuard: CanActivateFn = productAccessGuard(
	SOCIAL_LISTENING_ACCESS.product,
	SOCIAL_LISTENING_ACCESS.upsellPath
);

/**
 * Affiliates access gate — ARREARS ONLY, which is the whole of what is left.
 *
 * The department is reachable on every tier, free included: the platform's
 * revenue on affiliate work is a percentage of attributed sales rather than a
 * subscription, so there is nothing for a tier to gate. The backend says exactly
 * this with `arrears_only=True` on its own guard (`app/billing/access.py`), and
 * the one refusal it kept is the one kept here: a brand on the transaction-fee
 * model is SUSPENDED by having its zero-price subscription moved to `past_due`,
 * and that is a "you owe us money" gate rather than a plan gate.
 *
 * So the upsell page is still where an unpaid bill lands — it reads the arrears
 * state itself and tells the brand to settle rather than to subscribe — and
 * everybody else walks in. An unreadable billing state fails open.
 */
export const affiliatesAccessGuard: CanActivateFn = (): Observable<
	boolean | UrlTree
> => {
	const subscriptionsState = inject(SubscriptionsStateService);
	const router = inject(Router);

	return subscriptionsState.ensureLoaded().pipe(
		map(() => {
			if (billingStateIsUnknown(subscriptionsState)) {
				return true;
			}
			if (
				subscriptionsState.isProductPastDue(AFFILIATES_ACCESS.product)
			) {
				return router.parseUrl(AFFILIATES_ACCESS.upsellPath);
			}
			return true;
		}),
		catchError(() => of(true))
	);
};
